/**
 * Granola connect step (plan §7.2, §1.1). No OAuth — Granola issues a pasted
 * `grn_…` API key, so this is a form: paste the key, validate it live via the
 * connect endpoint, and show back what it can see BEFORE any seeding starts.
 *
 * That count is the whole point of R11: Granola has two key types on
 * adjacent settings pages, and the wrong one silently yields almost nothing
 * — a real WORKSPACE key saw 12 notes where a PERSONAL key on the same
 * account saw 938. Catching that in seconds (this screen) beats discovering
 * an empty index later. The server hands back only the raw facts
 * (visibleNoteCount, hasMore); deciding whether that reads as "probably a
 * workspace key" and rendering the warning is this component's job.
 */
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, NotebookPen } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, type GranolaConnectResult, granolaAccountApi } from '@/lib/api';

interface Props {
  onNext: () => void;
}

export function StepGranolaConnect({ onNext }: Props) {
  const [apiKey, setApiKey] = useState('');
  const [label, setLabel] = useState('');
  const [result, setResult] = useState<GranolaConnectResult | null>(null);

  const connect = useMutation({
    mutationFn: () => granolaAccountApi.connect(apiKey.trim(), label.trim() || undefined),
    onSuccess: (res) => setResult(res),
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 400 && err.message === 'invalid_key_format') {
          toast.error('That doesn\'t look like a Granola key — it should start with "grn_".');
          return;
        }
        if (err.status === 401) {
          toast.error(
            (err.body as { message?: string } | null)?.message ??
              'That key was rejected. Granola API keys need a Business or Enterprise plan.',
          );
          return;
        }
        if (err.status === 429) {
          toast.error("You've reached the Granola account limit, or Granola is rate-limiting.");
          return;
        }
        if (err.status === 503) {
          toast.error('Granola connections are not configured on this server yet.');
          return;
        }
      }
      toast.error("Couldn't connect that key. Please try again.");
    },
  });

  // Step 2: the connect result — the count, and the R11 warning when it's
  // suspiciously low. hasMore:false ⇒ visibleNoteCount is the EXACT total
  // (the probe pages at size 30), which is the workspace-key signature.
  if (result) {
    const likelyWorkspaceKey = !result.hasMore;
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border p-8">
          <IconTile tone="green">
            <NotebookPen className="h-5 w-5" />
          </IconTile>
          <h2 className="mt-4 font-semibold text-lg">Granola connected</h2>

          <Alert variant={likelyWorkspaceKey ? 'warning' : 'success'} className="mt-4">
            {likelyWorkspaceKey ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <AlertTitle>
              {result.hasMore
                ? 'This key can see more than 30 notes'
                : `This key can see ${result.visibleNoteCount} note${result.visibleNoteCount === 1 ? '' : 's'}`}
            </AlertTitle>
            <AlertDescription>
              {likelyWorkspaceKey ? (
                <>
                  That&apos;s a small, exact count — the signature of a <strong>workspace</strong>{' '}
                  key, which only reads notes explicitly shared with the whole team, not your own
                  meetings. If that&apos;s not what you expected, disconnect from Sources and
                  reconnect with a <strong>personal</strong> API key (Personal notes scope) instead.
                </>
              ) : (
                <>
                  That looks like a personal key. Brain is indexing your meeting notes and
                  transcripts in the background — track progress anytime from Sources.
                </>
              )}
            </AlertDescription>
          </Alert>

          <Button size="cta" className="mt-6 w-full" onClick={onNext}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Step 1: the paste-key form.
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="green">
          <NotebookPen className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 font-semibold text-lg">Connect Granola</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Paste an API key from Granola desktop to index your meeting notes and transcripts.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (apiKey.trim() && !connect.isPending) connect.mutate();
          }}
          className="mt-6 space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="granola-api-key">Personal API key</Label>
            <Input
              id="granola-api-key"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="grn_…"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-[12.5px] text-faint">
              Where do I find this? Granola desktop → Settings → Connectors → API keys.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="granola-label">Label (optional)</Label>
            <Input
              id="granola-label"
              placeholder="My Granola"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          {/* §7.3 required copy — these are launch requirements, not polish.
              All four must sit here, at connect time, not buried in settings. */}
          <div className="space-y-3 rounded-lg border border-border-subtle bg-muted/40 p-4 text-[13px] text-muted-foreground">
            <p>
              <span className="font-semibold text-foreground">
                Create a PERSONAL API key with the Personal notes scope.
              </span>{' '}
              A workspace key only reads notes shared with your whole team, not your own meetings.
            </p>
            <p>
              Everyone in this Brain workspace will be able to search these meeting notes and
              transcripts.
            </p>
            {/* D5 — the most important line on the screen. */}
            <p>
              <span className="font-semibold text-foreground">
                Brain keeps its own permanent copy.
              </span>{' '}
              Notes you delete in Granola, and transcripts removed by Granola&apos;s auto-deletion,
              stay searchable in Brain. Remove them here with the per-meeting toggle, or with{' '}
              <span className="font-semibold text-foreground">Delete all Granola data</span>.
            </p>
            <p>
              Disconnecting removes Brain&apos;s copy of the key but can&apos;t revoke it. Revoke it
              in Granola → Settings → Connectors → API keys.
            </p>
            <p>Granola API keys need a Business or Enterprise plan.</p>
          </div>

          <Button
            type="submit"
            size="cta"
            className="w-full"
            disabled={connect.isPending || !apiKey.trim()}
          >
            {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect Granola'}
          </Button>
        </form>
      </div>
    </div>
  );
}
